import Close from '@mui/icons-material/Close';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { ComposerAttachment } from '~hooks/useComposerAttachments/useComposerAttachments';

const THUMBNAIL_SIZE_IN_PX = 28;

interface ComposerAttachmentsProps {
  attachments: ComposerAttachment[];
  onRemove: (attachmentId: string) => void;
}

function StatusIndicator({ status }: { status: ComposerAttachment['status'] }) {
  const theme = useTheme();

  if (status === 'uploading') {
    return <CircularProgress size={14} />;
  }

  if (status === 'error') {
    return <ErrorOutline sx={{ color: theme.brandV2.colors.treadRed, fontSize: 16 }} />;
  }

  return null;
}

// Pending file attachments shown above the composer input while the user is drafting a
// message. Each chip previews the file, reports its upload status, and can be removed.
export default function ComposerAttachments({
  attachments,
  onRemove,
}: ComposerAttachmentsProps) {
  const theme = useTheme();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 0.25, py: 0.25 }}>
      {attachments.map((attachment) => (
        <Box
          key={attachment.id}
          sx={{
            alignItems: 'center',
            bgcolor: theme.brandV2.colors.treadGray9,
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: theme.brandV2.borderRadius,
            display: 'flex',
            gap: 0.5,
            maxWidth: '180px',
            pl: 0.5,
            pr: 0.25,
            py: 0.25,
          }}
        >
          {attachment.isImage && attachment.previewUrl ? (
            <Box
              alt={attachment.filename}
              component="img"
              src={attachment.previewUrl}
              sx={{
                borderRadius: '4px',
                height: `${THUMBNAIL_SIZE_IN_PX}px`,
                objectFit: 'cover',
                width: `${THUMBNAIL_SIZE_IN_PX}px`,
              }}
            />
          ) : (
            <InsertDriveFileOutlined
              sx={{ color: theme.brandV2.colors.treadGray3, fontSize: 16 }}
            />
          )}

          <Typography
            noWrap
            sx={{ flexGrow: 1, fontSize: '12px', minWidth: 0 }}
            title={attachment.filename}
          >
            {attachment.filename}
          </Typography>

          <StatusIndicator status={attachment.status} />

          <IconButton
            aria-label={t('actions.remove')}
            onClick={() => onRemove(attachment.id)}
            size="small"
            sx={{ p: 0.25 }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}
